import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const HiddenPassionNumberCalculator = () => {
  const [fullName, setFullName] = useState('');
  const [hiddenPassionNumber, setHiddenPassionNumber] = useState(null);
  const [error, setError] = useState('');
  const [chartData, setChartData] = useState(null);
  const [numberCounts, setNumberCounts] = useState({
    1: 0, 2: 0, 3: 0, 4: 0, 5: 0,
    6: 0, 7: 0, 8: 0, 9: 0
  });

  const handleInputChange = (e) => {
    setFullName(e.target.value);
    setError('');
    setNumberCounts({
      1: 0, 2: 0, 3: 0, 4: 0, 5: 0,
      6: 0, 7: 0, 8: 0, 9: 0
    });
  };

  const calculateHiddenPassionNumber = () => {
    if (!fullName.trim()) {
      setError('Please enter your full name');
      return;
    }

    // Remove spaces and convert to uppercase
    const name = fullName.replace(/\s+/g, '').toUpperCase();

    // Letter to number mapping for hidden passion calculation
    const letterValues = {
      A: 1, J: 1, S: 1,
      B: 2, K: 2, T: 2,
      C: 3, L: 3, U: 3,
      D: 4, M: 4, V: 4,
      E: 5, N: 5, W: 5,
      F: 6, O: 6, X: 6,
      G: 7, P: 7, Y: 7,
      H: 8, Q: 8, Z: 8,
      I: 9, R: 9
    };

    // Calculate counts for each number
    const tempCounts = { ...numberCounts };
    name.split('').forEach(letter => {
      const value = letterValues[letter];
      if (value) {
        tempCounts[value]++;
      }
    });
    setNumberCounts(tempCounts);

    // Find the highest count
    let maxCount = 0;
    let hiddenPassionNumbers = [];

    Object.entries(tempCounts).forEach(([number, count]) => {
      if (count > maxCount) {
        maxCount = count;
        hiddenPassionNumbers = [parseInt(number)];
      } else if (count === maxCount) {
        hiddenPassionNumbers.push(parseInt(number));
      }
    });

    // If there are multiple numbers with the same count, add them
    setHiddenPassionNumber(hiddenPassionNumbers);

    // Generate chart data
    setChartData({
      labels: Object.keys(tempCounts),
      datasets: [{
        label: 'Letter Occurrences',
        data: Object.values(tempCounts),
        backgroundColor: [
          'rgba(54, 162, 235, 0.5)',
          'rgba(255, 99, 132, 0.5)',
          'rgba(153, 102, 255, 0.5)',
          'rgba(75, 192, 192, 0.5)',
          'rgba(255, 206, 86, 0.5)',
          'rgba(255, 159, 64, 0.5)',
          'rgba(192, 192, 192, 0.5)',
          'rgba(128, 128, 128, 0.5)',
          'rgba(0, 0, 0, 0.5)'
        ],
        borderColor: [
          'rgba(54, 162, 235, 1)',
          'rgba(255, 99, 132, 1)',
          'rgba(153, 102, 255, 1)',
          'rgba(75, 192, 192, 1)',
          'rgba(255, 206, 86, 1)',
          'rgba(255, 159, 64, 1)',
          'rgba(192, 192, 192, 1)',
          'rgba(128, 128, 128, 1)',
          'rgba(0, 0, 0, 1)'
        ],
        borderWidth: 1
      }]
    });
  };

  const hiddenPassionMeanings = {
    1: 'Creative expression and leadership',
    2: 'Harmony and cooperation',
    3: 'Communication and self-expression',
    4: 'Practicality and stability',
    5: 'Freedom and adventure',
    6: 'Nurturing and responsibility',
    7: 'Introspection and spirituality',
    8: 'Ambition and success',
    9: 'Humanitarianism and compassion'
  };

  return (
    <div className="math-calculators max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <Helmet>
        <title>Hidden Passion Number Calculator | Discover Your Inner Desires</title>
        <meta name="description" content="Calculate your Hidden Passion Number using our Numerology Calculator. Discover your inner desires and hidden talents." />
        <meta name="keywords" content="hidden passion number, numerology calculator, inner desires, hidden talents" />
      </Helmet>

      <div className="calculator-header mb-12">
        <div className="flex items-center gap-4">
          <div className="header-icon text-4xl">🔥</div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Hidden Passion Number Calculator</h1>
            <p className="text-lg text-blue-600 font-semibold">Discover Your Inner Desires</p>
          </div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="calculator-form max-w-2xl mx-auto bg-white rounded-xl shadow-lg p-8"
      >
        <div className="input-group mb-6">
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="fullName">
            Full Name:
          </label>
          <input
            type="text"
            id="fullName"
            name="fullName"
            value={fullName}
            onChange={handleInputChange}
            className="appearance-none block w-full bg-gray-200 text-gray-700 border rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
            placeholder="Enter your full name"
          />
        </div>

        <button
          onClick={calculateHiddenPassionNumber}
          className="w-full bg-blue-500 hover:bg-blue-600 text-white font-bold py-3 px-4 rounded focus:outline-none focus:bg-white"
        >
          Calculate Hidden Passion Number
        </button>

        {error && (
          <div className="text-red-500 text-center mt-4">
            {error}
          </div>
        )}

        {hiddenPassionNumber && (
          <div className="results-section mt-12">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Your Hidden Passion Numbers</h3>
            <div className="text-center">
              <div className="text-6xl font-bold text-blue-600 mb-4">
                {hiddenPassionNumber.join(', ')}
              </div>
              <p className="text-gray-700">
                These numbers reveal your inner desires and hidden talents.
              </p>
            </div>
            {chartData && (
              <div className="mt-8 space-y-8">
                <div>
                  <h4 className="text-xl font-semibold mb-4">Letter Distribution</h4>
                  <div className="h-96 rounded-lg overflow-hidden shadow-lg">
                    <Bar
                      data={chartData}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: {
                          title: {
                            display: true,
                            text: 'Letter Distribution in Your Name',
                            color: '#3B82F6',
                            font: {
                              size: 18,
                              weight: 'bold'
                            }
                          },
                          legend: {
                            display: false
                          }
                        },
                        scales: {
                          x: {
                            grid: {
                              display: false
                            },
                            ticks: {
                              color: '#6B7280',
                              font: {
                                size: 14
                              }
                            }
                          },
                          y: {
                            beginAtZero: true,
                            grid: {
                              color: '#E5E7EB'
                            },
                            ticks: {
                              color: '#6B7280',
                              font: {
                                size: 14
                              }
                            }
                          }
                        },
                        animation: {
                          duration: 1000,
                          easing: 'easeInOutQuad'
                        },
                        interaction: {
                          mode: 'index',
                          intersect: false
                        },
                        hover: {
                          mode: 'nearest',
                          intersect: true
                        }
                      }}
                    />
                  </div>
                  <div className="mt-4 grid grid-cols-3 gap-4">
                    {Object.entries(numberCounts).map(([number, count]) => (
                      <div key={number} className="p-4 bg-white rounded-lg shadow-inner">
                        <p className="font-medium text-gray-700">Number {number}</p>
                        <p className="text-2xl font-bold text-blue-600">{count}</p>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-gray-50 p-6 rounded-xl">
                  <h4 className="text-xl font-semibold mb-4">Example Calculation</h4>
                  <div className="space-y-4">
                    <p className="text-gray-700">
                      For the name "John Doe":
                    </p>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="p-4 bg-white rounded-lg shadow-inner">
                        <p className="font-medium">Letters:</p>
                        <p>J, O, H, N, D, O, E</p>
                      </div>
                      <div className="p-4 bg-white rounded-lg shadow-inner">
                        <p className="font-medium">Values:</p>
                        <p>1, 6, 8, 5, 4, 6, 5</p>
                      </div>
                      <div className="p-4 bg-white rounded-lg shadow-inner">
                        <p className="font-medium">Most Common:</p>
                        <p>6 (O appears twice)</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 p-6 rounded-xl">
                  <h4 className="text-xl font-semibold mb-4">Your Hidden Passion Meanings</h4>
                  <div className="space-y-4">
                    {hiddenPassionNumber.map((number, index) => (
                      <div key={index} className="p-4 bg-white rounded-lg shadow-inner">
                        <p className="font-medium">Number {number}:</p>
                        <p>{hiddenPassionMeanings[number]}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </motion.div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          How to Use <span className="ml-2 text-2xl">📋</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <p className="text-gray-700 mb-6">
            Follow these steps to calculate your Hidden Passion Number:
          </p>
          <div className="space-y-8">
            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 1: Enter Full Name</h3>
              <p className="text-gray-700">
                Input your complete full name (first, middle, last).
              </p>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 2: Calculate</h3>
              <p className="text-gray-700">
                Click the "Calculate" button to get your Hidden Passion Number.
              </p>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 3: Understand Results</h3>
              <p className="text-gray-700">
                Review your Hidden Passion Numbers and their meanings in the results section.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          FAQ <span className="ml-2 text-2xl">❓</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <div className="space-y-8">
            <div>
              <h3 className="text-lg font-semibold mb-2">What is a Hidden Passion Number?</h3>
              <p className="text-gray-700">
                Your Hidden Passion Number reveals your inner desires and hidden talents. It shows what truly motivates you on a subconscious level.
              </p>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-2">How is it calculated?</h3>
              <p className="text-gray-700">
                It's calculated by finding the most frequently occurring numbers in your name (based on letter values), where each letter corresponds to a number from 1 to 9.
              </p>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-2">What's the difference between Hidden Passion and other numerology numbers?</h3>
              <p className="text-gray-700">
                The Hidden Passion Number represents your inner desires and subconscious motivations, while other numbers like Expression and Personality focus on different aspects of your being.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          Features & Benefits <span className="ml-2 text-2xl">✨</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Key Features</h3>
              <ul className="list-disc pl-8 space-y-3">
                <li>Accurate Hidden Passion Number calculation</li>
                <li>Visual letter distribution analysis</li>
                <li>Supports multiple hidden passion numbers</li>
                <li>Clean and intuitive interface</li>
                <li>Detailed explanation of results</li>
              </ul>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Benefits</h3>
              <ul className="list-disc pl-8 space-y-3">
                <li>Discover your inner desires</li>
                <li>Understand your hidden talents</li>
                <li>Learn what truly motivates you</li>
                <li>Align with your subconscious goals</li>
                <li>Make decisions that align with your passions</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HiddenPassionNumberCalculator;
